import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "shared/lib/config";
import RadarChart from "features/analytics/components/RadarChart";
import MinMaxChart from "features/analytics/components/MinMaxChart";
import logo2 from "assets/images/potentialogo.png";


/* --------------------------- helpers & constants -------------------------- */

// Where to read the auth token
const getStore = () => {
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return null;
};

// "Vocabulary (Gc)" -> { name: "Vocabulary", code: "Gc" }
const parseMetric = (metric) => {
  if (!metric) return { name: metric, code: metric };
  const m = String(metric);
  const match = m.match(/\(([^)]+)\)\s*$/);
  const code = match ? match[1] : m;
  const name = m.replace(/\s*\([^)]+\)\s*$/, "");
  return { name, code };
};

// Normalize gender into 'male' | 'female' | ''
const normalizeGender = (g) => {
  if (!g) return "";
  const s = String(g).trim().toLowerCase();
  if (["male", "laki-laki", "laki", "boy", "m", "l"].includes(s)) return "male";
  if (["female", "perempuan", "wanita", "girl", "f", "p"].includes(s)) return "female";
  return "";
};

// Preferred order for the 8 factors (case-insensitive for GV/Gv)
const CODE_ORDER = ["GC", "GF", "GL", "GWM", "GR", "GA", "GV", "GS"];

// Page size options
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, "all"];

/** Format a number with 1 decimal, but trim any trailing ".0" */
const fmt1 = (n) => {
  if (!Number.isFinite(n)) return "--";
  const rounded = Math.round(n * 10) / 10;
  const s = rounded.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
};

export default function ClassAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: classKey } = useParams();

  // If Teacher.jsx navigated with state, we can use it directly
  const navClassInfo = location.state?.classInfo || null;
  const navAssessments = location.state?.assessments || null;

  const [status, setStatus] = useState(navAssessments ? "" : "Memuat...");
  const [raw, setRaw] = useState(navAssessments || null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [classInfo, setClassInfo] = useState(
    navClassInfo || { name: "Class", academicYear: null }
  );

  // refs for smooth scroll after selection on mobile
  const analysisRef = useRef(null);

  // Table controls
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("name"); // 'name' | 'age' | factor code
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);   // page size selector

  // Filters
  const [genderFilter, setGenderFilter] = useState("all"); // 'all' | 'male' | 'female'
  const [ageFilter, setAgeFilter] = useState("all");       // 'all' | number as string
  const [filtersOpen, setFiltersOpen] = useState(false);   // mobile filters disclosure
  const [filtersOpenDesktop, setFiltersOpenDesktop] = useState(false);

  // Fetch when we don't receive state (deep-link / refresh)
  useEffect(() => {
    if (navAssessments) {
      setRaw(navAssessments);
      setClassInfo(navClassInfo || { name: "Class", academicYear: null });
      setStatus("");
      return;
    }

    const init = async () => {
      const store = getStore();
      const token = store?.getItem("idToken");
      if (!token || !store) {
        navigate("/", { replace: true });
        return;
      }
      try {
        const res = await fetch(`${BASE_URL}/api/assessments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.message || "Failed to load");

        const arr = Array.isArray(payload) ? payload : payload?.data || [];

        // Accept ObjectId OR slug
        const isObjectId = /^[0-9a-fA-F]{24}$/.test(String(classKey));

        const filtered = arr.filter((r) => {
          const cls = r?.classId;
          const id = (cls && cls._id) || r?.classId;
          const slug = cls?.slug;
          return isObjectId ? String(id) === String(classKey) : String(slug) === String(classKey);
        });

        const metaCls = filtered[0]?.classId || null;

        setClassInfo(
          metaCls
            ? {
                name: metaCls.name || "Class",
                academicYear: metaCls.academicYear || null,
                slug: metaCls.slug || null,
              }
            : { name: "Class", academicYear: null, slug: null }
        );

        setRaw(filtered);

        setStatus("");
      } catch (err) {
        console.error(err);
        setStatus("Failed to load");
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classKey]);

  /* ---------------------------- derive class data ---------------------------- */

  const {
    className,
    classYear,
    students,    // [{id,name,email,voucher,gender,age,scores:{code->value}}]
    categories,  // [{code,name}]
    ageOptions,  // unique ages for dropdown
    preloadByRowId, // NEW: { [rowId]: latestAssessmentObject }
  } = useMemo(() => {
    const result = {
      className: classInfo?.name || "Class",
      classYear:
        (typeof classInfo?.academicYear === "object"
          ? classInfo?.academicYear?.name
          : classInfo?.academicYear) || null,
      students: [],
      categories: [],
      ageOptions: [],
      preloadByRowId: {},  // <— keep the exact latest assessment per table row
    };
    if (!Array.isArray(raw) || raw.length === 0) return result;
    const ages = new Set();

    const ayName =
      (typeof classInfo?.academicYear === "object"
        ? classInfo?.academicYear?.name
        : classInfo?.academicYear) || null;

    const inThisAY = ayName
      ? raw.filter((r) => {
          const docAY =
            typeof r?.academicYearId === "object"
              ? r?.academicYearId?.name
              : r?.academicYearId;
          return String(docAY) === String(ayName);
        })
      : raw;

    // Build category set
    const catMap = new Map();
    for (const rec of inThisAY) {
      for (const s of rec?.scores || []) {
        const { name, code } = parseMetric(s.metric);
        const codeNorm = code?.toString().toUpperCase();
        if (!catMap.has(codeNorm)) catMap.set(codeNorm, { code: codeNorm, name });
      }
    }

    // Sort categories by preferred order
    result.categories = Array.from(catMap.values()).sort((a, b) => {
      const ia = CODE_ORDER.indexOf(a.code);
      const ib = CODE_ORDER.indexOf(b.code);
      if (ia === -1 && ib === -1) return a.code.localeCompare(b.code);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });

    // Pick latest assessment per TEST TAKER NAME (in this AY)
    const byTaker = new Map(); // key = normalized test_taker_name
    for (const rec of inThisAY) {
      const taker = (rec?.test_taker_name || "").trim();
      if (!taker) continue;
      const prev = byTaker.get(taker);
      const prevDate = prev?.createdAtVendor ? new Date(prev.createdAtVendor).getTime() : -Infinity;
      const currDate = rec?.createdAtVendor ? new Date(rec.createdAtVendor).getTime() : -Infinity;
      if (!prev || currDate >= prevDate) byTaker.set(taker, rec);
    }

    // Normalize rows + build preload map
    for (const [, rec] of byTaker.entries()) {
      const parent = rec.studentId || {};
      const scoresObj = {};
      (rec.scores || []).forEach((sc) => {
        const { code } = parseMetric(sc.metric);
        scoresObj[String(code).toUpperCase()] = sc.score;
      });

      // use assessment fields (NOT parent user)
      const gNorm = normalizeGender(rec.gender);
      const a = Number.isFinite(Number(rec.age)) ? Number(rec.age) : null;
      if (a !== null) ages.add(a);

      const takerName = (rec.test_taker_name || "").trim() || `taker-${rec.voucher}`;
      const rowId = `${parent._id || "noparent"}::${takerName}`;

      result.students.push({
        id: rowId,
        name: takerName,
        email: parent.email || "",
        voucher: rec.voucher || "",
        gender: gNorm,
        age: a,
        scores: scoresObj,
      });

      // Keep the *exact* latest assessment so Report.jsx can render instantly
      result.preloadByRowId[rowId] = rec;
    }

    result.ageOptions = Array.from(ages).sort((x, y) => x - y);
    return result;
  }, [raw, classInfo]);

  // Default select first student when students change
  useEffect(() => {
    if (students.length && !selectedStudentId) {
      setSelectedStudentId(students[0].id);
    }
  }, [students, selectedStudentId]);

  // Reset page when controls change
  useEffect(() => {
    setPage(1);
  }, [query, sortKey, sortDir, genderFilter, ageFilter]);

  // Reset page when page size changes
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  /* ----------------------- aggregates & selections ----------------------- */

  const { distribution, strengths, weaknesses } = useMemo(() => {
    const dist = categories.map((c) => {
      const vals = students
        .map((s) => s.scores[c.code])
        .filter((v) => typeof v === "number");

      const avg =
        vals.length > 0
          ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
          : 0;
      const max = vals.length ? Math.max(...vals) : 0;
      const min = vals.length ? Math.min(...vals) : 0;

      return { ...c, average: avg, max, min };
    });

    const sorted = [...dist].sort((a, b) => b.average - a.average);
    return {
      distribution: dist,
      strengths: sorted.slice(0, 2),
      weaknesses: sorted.slice(-2),
    };
  }, [categories, students]);

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === selectedStudentId),
    [students, selectedStudentId]
  );

  /* -------------------------- table computed rows -------------------------- */

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = !q
      ? [...students]
      : students.filter((s) => (s.name || "").toLowerCase().includes(q));

    // gender filter
    if (genderFilter !== "all") {
      rows = rows.filter((s) => s.gender === genderFilter);
    }

    // age filter
    if (ageFilter !== "all") {
      const target = Number(ageFilter);
      rows = rows.filter((s) => s.age === target);
    }

    // sort
    rows.sort((a, b) => {
      if (sortKey === "name") {
        const an = (a.name || "").toLowerCase();
        const bn = (b.name || "").toLowerCase();
        return sortDir === "asc" ? an.localeCompare(bn) : bn.localeCompare(an);
      }
      if (sortKey === "age") {
        const av = Number.isFinite(a.age) ? a.age : -Infinity;
        const bv = Number.isFinite(b.age) ? b.age : -Infinity;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const av = typeof a.scores[sortKey] === "number" ? a.scores[sortKey] : -Infinity;
      const bv = typeof b.scores[sortKey] === "number" ? b.scores[sortKey] : -Infinity;
      return sortDir === "asc" ? av - bv : bv - av;
    });

    return rows;
  }, [students, query, sortKey, sortDir, genderFilter, ageFilter]);

  const total = filteredSorted.length;
  const effectivePageSize = pageSize === "all" ? total : Number(pageSize) || 10;
  const totalPages = Math.max(1, Math.ceil(total / (effectivePageSize || 1)));
  const currentPage = Math.min(page, totalPages);
  const startIdx = total ? (currentPage - 1) * effectivePageSize : 0;
  const endIdx = total ? Math.min(startIdx + effectivePageSize, total) : 0;
  const pageRows = filteredSorted.slice(startIdx, endIdx);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  /* ------------------------------ mobile helpers ------------------------------ */

  const handleSelectMobile = (stuId) => {
    setSelectedStudentId(stuId);
    // Smooth scroll to analysis panel after selection (mobile only)
    if (window.innerWidth < 768 && analysisRef.current) {
      // delay a frame so the section updates first
      requestAnimationFrame(() => {
        analysisRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  // Reusable navigate helper so both mobile & desktop buttons preload the assessment
  const gotoReport = (stu) => {
    const preloaded = preloadByRowId[stu.id] || null;
    const search = preloaded?.voucher
      ? `?voucher=${encodeURIComponent(preloaded.voucher)}`
      : `?studentId=${encodeURIComponent(stu.id)}`;

    navigate(`/report${search}`, {
      state: {
        from: "class-analytics",
        studentName: stu.name,
        voucher: preloaded?.voucher || null,
        preloadedAssessment: preloaded || null, // ← THIS makes Report.jsx show data instantly
      },
    });
  };

  /* ---------------------------------- UI ---------------------------------- */

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header */}
      <header
        className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-200/50"
        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
      >
        <div className="max-w-7xl mx-auto">
          {/* ========== MOBILE (sm:hidden) ========== */}
          <div className="sm:hidden px-4 py-2">
            {/* Row 1: back + logo | students chip */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-xl hover:bg-blue-50 active:scale-95 transition"
                  aria-label="Back"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Reserve intrinsic ratio for the logo to avoid reflow */}
                <img src={logo2} alt="Potentia" width="112" height="28" className="h-7 w-auto" />
              </div>

              <div className="text-[11px] text-gray-700 bg-white/80 rounded-full px-2 py-1 shadow-sm border border-gray-200/60">
                {students.length} siswa
              </div>
            </div>

            {/* Row 2: centered title + year */}
            <div className="mt-2 text-center">
              <h1 className="text-base font-bold text-slate-900 leading-tight">
                Laporan Analisis — {className}
              </h1>
              <div className="text-xs text-gray-500">{classYear || "—"}</div>

              {/* subtle divider */}
              <div className="mx-auto mt-2 h-[6px] w-11/12 rounded-full bg-gradient-to-r from-transparent via-slate-200/80 to-transparent" />
            </div>
          </div>

          {/* ========== DESKTOP/TABLET (hidden on mobile) ========== */}
          {/* Avoid layout shift from hidden→block by reserving height & keeping it in flow */}
          <div className="relative sm:block invisible sm:visible min-h-[64px]">
            <div className="hidden sm:block">
              <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center gap-3 md:gap-4">
                  <button
                    onClick={() => navigate(-1)}
                    className="p-2 hover:bg-blue-50 rounded-xl transition-colors group"
                    aria-label="Back"
                  >
                    <svg
                      className="w-5 h-5 text-gray-600 group-hover:text-blue-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Intrinsic sizes to prevent reflow */}
                  <img src={logo2} alt="Potentia" width="128" height="32" className="h-7 md:h-8 w-auto" />

                  <div className="flex items-center gap-2">
                    <div className="hidden sm:flex w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18M3 9h18M3 15h18M3 21h18" />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-lg md:text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                        Laporan Analisis - {className}
                      </h1>
                      <p className="text-xs md:text-sm text-gray-500 font-medium">
                        {classYear || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-700 bg-white/70 rounded-full px-3 md:px-4 py-1.5 border border-gray-200/60 shadow-sm">
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 20h-2a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM7 20H5a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM3 10h18M7 10V6a5 5 0 1110 0v4"
                    />
                  </svg>
                  <span>{students.length} Siswa</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Loading / error */}
      {status && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 text-gray-600">{status}</div>
      )}

      {!status && (
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6 md:space-y-8">
          {/* Strengths / Weaknesses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-5 md:p-6 text-white shadow-lg">
              <div className="flex items-center space-x-3 mb-3 md:mb-4">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 8-8" />
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl font-bold">Kemampuan Utama Kelas</h3>
              </div>
              <div className="space-y-2 md:space-y-3">
                {strengths.length ? strengths.map((it) => (
                  <div key={it.code} className="flex justify-between items-center italic">
                    <span className="font-medium">{it.name} ({it.code})</span>
                    <div className="bg-white/20 rounded-full px-2.5 md:px-3 py-0.5 md:py-1 text-sm font-semibold">
                      {fmt1(it.average)}
                    </div>
                  </div>
                )) : <div className="text-white/80">No data</div>}
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-5 md:p-6 text-white shadow-lg">
              <div className="flex items-center space-x-3 mb-3 md:mb-4">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl font-bold">Area yang Perlu Dikembangkan</h3>
              </div>
              <div className="space-y-2 md:space-y-3">
                {weaknesses.length ? weaknesses.map((it) => (
                  <div key={it.code} className="flex justify-between items-center italic">
                    <span className="font-medium">{it.name} ({it.code})</span>
                    <div className="bg-white/20 rounded-full px-2.5 md:px-3 py-0.5 md:py-1 text-sm font-semibold">
                      {fmt1(it.average)}
                    </div>
                  </div>
                )) : <div className="text-white/80">No data</div>}
              </div>
            </div>
          </div>

          {/* Distribution bars (Avg + Max + Min) */}
          {/* Reserve height & contain overflow to avoid push-down on chart mount */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden relative z-30 min-h-[360px] md:min-h-[420px]">
            <div className="h-full">
              <MinMaxChart distribution={distribution} title="Distribusi Performa Kelas" />
            </div>
          </div>
          
          {/* Students section */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
            {/* Section header */}
            <div className="px-5 md:px-8 pt-5 md:pt-6 pb-3 border-b border-gray-200/50 bg-gradient-to-r from-gray-50 to-white">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h-2a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM7 20H5a2 2 0 01-2-2v-3h6v3a2 2 0 01-2 2zM3 10h18M7 10V6a5 5 0 1110 0v4" />
                  </svg>
                </div>
                <span>Performa Siswa</span>
              </h3>

              {/* Mobile filters toggle */}
              <div className="mt-3 md:hidden">
                {/* Always-visible search (above the dropdown) */}
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cari nama siswa…"
                  className="w-full rounded-xl border border-gray-200 bg-white/70 px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />

                {/* Dropdown toggle */}
                <button
                  className="mt-2 w-full inline-flex items-center justify-between rounded-xl border border-gray-200 bg-white/70 px-3 py-2 text-sm shadow-sm"
                  onClick={() => setFiltersOpen((v) => !v)}
                >
                  <span className="text-gray-700">Filter & Urutkan</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${filtersOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown content: only selects */}
                {filtersOpen && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <select
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value)}
                      className="rounded-xl border border-gray-200 bg-white/70 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="name">Nama</option>
                      <option value="age">Umur</option>
                      {categories.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name} ({c.code})
                        </option>
                      ))}
                    </select>

                    <select
                      value={sortDir}
                      onChange={(e) => setSortDir(e.target.value)}
                      className="rounded-xl border border-gray-200 bg-white/70 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="desc">Z - A</option>
                      <option value="asc">A - Z</option>
                    </select>

                    <select
                      value={genderFilter}
                      onChange={(e) => setGenderFilter(e.target.value)}
                      className="rounded-xl border border-gray-200 bg-white/70 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="all">Jenis Kelamin</option>
                      <option value="male">Laki-laki</option>
                      <option value="female">Perempuan</option>
                    </select>

                    <select
                      value={ageFilter}
                      onChange={(e) => setAgeFilter(e.target.value)}
                      className="rounded-xl border border-gray-200 bg-white/70 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="all">Umur</option>
                      {ageOptions.map((a) => (
                        <option key={a} value={String(a)}>{a}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Desktop control bar */}
              <div className="mt-6 hidden md:flex flex-wrap items-center gap-6">
                {/* Search */}
                <div className="relative flex-1 min-w-[260px] max-w-lg ml-6">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Cari nama siswa..."
                    className="w-full rounded-xl border border-gray-200 bg-white/70 px-10 py-2 text-sm text-gray-700 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  <svg
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </div>

                {/* Sorting */}
                <div className="flex items-center gap-2 ml-4">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pengurutan</span>

                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                    className="w-36 rounded-lg border border-gray-200 bg-white/70 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="name">Nama</option>
                    <option value="age">Umur</option>
                    {categories.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>

                  <select
                    value={sortDir}
                    onChange={(e) => setSortDir(e.target.value)}
                    className="w-28 rounded-lg border border-gray-200 bg-white/70 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="asc">A - Z</option>
                    <option value="desc">Z - A</option>
                  </select>
                </div>

                {/* Filter */}
                <div className="flex items-center gap-2 ml-10">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filter</span>

                  <select
                    value={genderFilter}
                    onChange={(e) => setGenderFilter(e.target.value)}
                    className="w-44 rounded-lg border border-gray-200 bg-white/70 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="all">Jenis Kelamin</option>
                    <option value="male">Laki-laki</option>
                    <option value="female">Perempuan</option>
                  </select>

                  <select
                    value={ageFilter}
                    onChange={(e) => setAgeFilter(e.target.value)}
                    className="w-36 rounded-lg border border-gray-200 bg-white/70 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="all">Umur</option>
                    {ageOptions.map((a) => (
                      <option key={a} value={String(a)}>{a}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Mobile cards (md:hidden) */}
            <div className="md:hidden divide-y divide-gray-100">
              {pageRows.length ? pageRows.map((stu) => {
                const isSelected = selectedStudentId === stu.id;
                return (
                  <div
                    key={stu.id}
                    className={`w-full px-4 py-3 ${isSelected ? "bg-blue-50" : "active:bg-blue-50"} transition`}
                  >
                    {/* Top row = select (not navigate) */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectMobile(stu.id)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleSelectMobile(stu.id); }}
                      className="flex items-start justify-between gap-3"
                    >
                      <div>
                        <div className="font-semibold text-gray-900">{stu.name}</div>
                        <div className="mt-1 flex items-center gap-2 text-xs">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                              stu.gender === "male"
                                ? "bg-blue-100 text-blue-800"
                                : stu.gender === "female"
                                ? "bg-pink-100 text-pink-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {stu.gender === "male" ? "Laki-laki" : stu.gender === "female" ? "Perempuan" : "—"}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            Umur: {Number.isFinite(stu.age) ? stu.age : "—"}
                          </span>
                        </div>
                      </div>

                      {/* details button (navigate) */}
                      <button
                        className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow"
                        onClick={(e) => {
                          e.stopPropagation();
                          gotoReport(stu);
                        }}
                        aria-label={`Lihat rincian ${stu.name}`}
                      >
                        Lihat Rincian
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Tiny scores strip */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {categories.map((c) => {
                        const sc = stu.scores[c.code];
                        return (
                          <span
                            key={c.code}
                            className={`px-2 py-0.5 rounded-full text-[11px] ${
                              typeof sc === "number"
                                ? sc >= 100
                                  ? "bg-green-100 text-green-800"
                                  : sc >= 90
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {c.code}: {typeof sc === "number" ? Math.round(sc).toString() : "--"}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              }) : (
                <div className="px-4 py-6 text-center text-gray-500">Tidak ada siswa yang ditemukan.</div>
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                  <tr>
                    {/* renamed header */}
                    <th className="px-6 py-4 text-left font-semibold">Nama Siswa</th>
                    {categories.map((c) => (
                      <th key={c.code} className="px-4 py-4 text-center font-semibold text-sm">
                        {c.code}
                      </th>
                    ))}
                    <th className="px-6 py-4 text-center font-semibold text-sm">Rincian</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pageRows.map((stu, idx) => (
                    <tr
                      key={stu.id}
                      className={`transition-all duration-200 hover:bg-blue-50 ${
                        selectedStudentId === stu.id
                          ? "bg-gradient-to-r from-blue-100 to-blue-50 ring-2 ring-blue-300"
                          : (startIdx + idx) % 2 === 0
                          ? "bg-white/50"
                          : "bg-gray-50/50"
                      }`}
                      onClick={() => setSelectedStudentId(stu.id)}
                    >
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-3">
                            {selectedStudentId === stu.id && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                            <span className="whitespace-nowrap">{stu.name}</span>
                          </div>

                          {/* Gender + Age badges */}
                          <div className="mt-1 flex items-center gap-2 text-xs">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                                stu.gender === "male"
                                  ? "bg-blue-100 text-blue-800"
                                  : stu.gender === "female"
                                  ? "bg-pink-100 text-pink-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                              title="Gender"
                            >
                              {stu.gender === "male" ? "Laki-laki" : stu.gender === "female" ? "Perempuan" : "—"}
                            </span>

                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                              title="Umur"
                            >
                              <span>{Number.isFinite(stu.age) ? `${stu.age}` : "—"}</span>
                            </span>
                          </div>
                        </div>
                      </td>

                      {categories.map((c) => {
                        const sc = stu.scores[c.code];
                        return (
                          <td key={c.code} className="px-4 py-4 text-center font-medium">
                            {typeof sc === "number" ? (
                              <span
                                className={`px-2 py-1 rounded-full text-sm ${
                                  sc >= 100
                                    ? "bg-green-100 text-green-800"
                                    : sc >= 90
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {fmt1(sc)}
                              </span>
                            ) : (
                              <span className="text-gray-400">--</span>
                            )}
                          </td>
                        );
                      })}

                      <td className="px-6 py-4">
                        <div className="flex justify-center">
                          <button
                            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-1.5 text-sm font-semibold text-white shadow hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition whitespace-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              gotoReport(stu);
                            }}
                            aria-label={`View report for ${stu.name}`}
                          >
                            Lihat Rincian
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {!pageRows.length && (
                    <tr>
                      <td
                        colSpan={1 + categories.length + 1}
                        className="px-6 py-6 text-center text-gray-500"
                      >
                        Tidak ada siswa yang ditemukan.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* pagination (bottom) */}
            <div className="px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-600">
              {/* Left: page size + showing range */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const v = e.target.value === "all" ? "all" : Number(e.target.value);
                      setPageSize(v);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt === "all" ? "All" : opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hidden md:block opacity-70">
                  Menunjukkan{" "}
                  <span className="font-semibold text-gray-700">{total ? startIdx + 1 : 0}</span>–
                  <span className="font-semibold text-gray-700">{endIdx}</span> dari{" "}
                  <span className="font-semibold text-gray-700">{total}</span>
                </div>
              </div>

              {/* Right: pager */}
              <div className="flex items-center gap-2">
                <button
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 hover:bg-gray-50 disabled:opacity-40"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>

                {/* hide number buttons on small screens */}
                <div className="hidden md:flex items-center gap-2">
                  {pageNumbers.map((n) => (
                    <button
                      key={n}
                      className={`h-8 w-8 rounded-lg border ${
                        n === currentPage
                          ? "border-blue-300 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white hover:bg-gray-50"
                      }`}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>

                <button
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 hover:bg-gray-50 disabled:opacity-40"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>

              {/* On small screens, show “showing” text under pager */}
              <div className="md:hidden text-center opacity-70">
                Menunjukkan{" "}
                <span className="font-semibold text-gray-700">{total ? startIdx + 1 : 0}</span>–
                <span className="font-semibold text-gray-700">{endIdx}</span> dari{" "}
                <span className="font-semibold text-gray-700">{total}</span>
              </div>
            </div>
          </div>

          {/* Student analysis + interactive radar */}
          <div ref={analysisRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Student Analysis */}
            {/* Reserve height to avoid push-down when content mounts */}
            <div className="bg-gradient-to-br from-white/80 to-blue-50/40 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/50 overflow-hidden min-h-[420px]">
              <div className="px-6 md:px-8 py-4 md:py-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <h3 className="text-xl md:text-2xl font-bold flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c1.657 0 3-1.567 3-3.5S13.657 1 12 1 9 2.567 9 4.5 10.343 8 12 8zM4 22a8 8 0 1116 0H4z" />
                    </svg>
                  </div>
                  <span>Analisis Siswa</span>
                </h3>
              </div>

              <div className="p-6 md:p-8">
                {selectedStudent ? (
                  (() => {
                    const s = selectedStudent;
                    const entries = Object.entries(s.scores || {});
                    const sorted = entries.sort((a, b) => b[1] - a[1]);
                    const top2 = sorted.slice(0, 2);
                    const bottom2 = sorted.slice(-2);
                    const overall =
                      entries.length > 0
                        ? entries.reduce((acc, [, v]) => acc + v, 0) / entries.length
                        : 0;

                    return (
                      <div className="space-y-6 md:space-y-8">
                        <div className="text-center">
                          <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 shadow-lg">
                            <svg className="w-7 h-7 md:w-8 md:h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c1.657 0 3-1.567 3-3.5S13.657 1 12 1 9 2.567 9 4.5 10.343 8 12 8zM4 22a8 8 0 1116 0H4z" />
                            </svg>
                          </div>
                          <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-1 md:mb-2">{s.name}</h4>
                          <div className="flex items-center justify-center gap-3 md:gap-4">
                            <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-full px-3 md:px-4 py-1.5">
                              <span className="text-blue-800 font-semibold text-sm">
                                Skor Keseluruhan: {fmt1(overall)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                          <div className="bg-emerald-50 rounded-xl p-4 md:p-5">
                            <div className="flex items-center gap-2 mb-2 md:mb-3">
                              <div className="p-2 bg-emerald-500 rounded-lg">
                                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 8-8" />
                                </svg>
                              </div>
                              <h5 className="font-bold text-emerald-800">Kemampuan Terbaik</h5>
                            </div>
                            <div className="space-y-2">
                              {top2.map(([code, val]) => {
                                const full = categories.find((c) => c.code === code)?.name || code;
                                return (
                                  <div key={code} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 shadow-sm italic">
                                    <span className="font-medium text-emerald-800">
                                      {full} ({code})
                                    </span>
                                    <span className="bg-emerald-500 text-white font-bold px-2 py-1 rounded-full text-sm">
                                      {fmt1(val)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="bg-orange-50 rounded-xl p-4 md:p-5">
                            <div className="flex items-center gap-2 mb-2 md:mb-3">
                              <div className="p-2 bg-orange-500 rounded-lg">
                                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                                </svg>
                              </div>
                              <h5 className="font-bold text-orange-800">Area Berkembang</h5>
                            </div>
                            <div className="space-y-2">
                              {bottom2.map(([code, val]) => {
                                const full = categories.find((c) => c.code === code)?.name || code;
                                return (
                                  <div key={code} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 shadow-sm italic">
                                    <span className="font-medium text-orange-800">
                                      {full} ({code})
                                    </span>
                                    <span className="bg-orange-500 text-white font-bold px-2 py-1 rounded-full text-sm">
                                      {fmt1(val)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-12 md:py-16 text-gray-600">
                    Pilih seorang siswa dari daftar untuk melihat analisanya.
                  </div>
                )}
              </div>
            </div>

            {/* Interactive Radar */}
            {/* Reserve height & contain overflow */}
            <div className="bg-gradient-to-br from-white/80 to-purple-50/40 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/50 overflow-hidden italic min-h-[420px]">
              <div className="px-6 md:px-8 py-4 md:py-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                <h3 className="text-xl md:text-2xl font-bold flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 19h4V9H4v10zm6 0h4V5h-4v14zm6 0h4V13h-4v6z" />
                    </svg>
                  </div>
                  <span>Performance Radar</span>
                </h3>
              </div>
              <div className="p-4 md:p-6">
                {selectedStudent ? (
                  <RadarChart
                    data={categories.map((c) => ({
                      metric: c.name,
                      code: c.code,
                      score:
                        typeof selectedStudent.scores[c.code] === "number"
                          ? selectedStudent.scores[c.code]
                          : 0,
                    }))}
                  />
                ) : (
                  <div className="text-gray-500">No data</div>
                )}
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}


          {/* Recommendations */}
          {/* <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-lg text-white overflow-hidden">
            <div className="px-8 py-6">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                  </svg>
                </div>
                <span>Rekomendasi Kelas</span>
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white/10 rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 8-8" />
                    </svg>
                    <span>Leverage Strengths</span>
                  </h4>
                  <p className="text-blue-100 leading-relaxed">
                    Focus on building upon the class's strongest areas:{" "}
                    <strong>
                      {strengths.map((s) => `${s.name} (${s.code})`).join(" and ") || "—"}
                    </strong>. Use peer tutoring and advanced challenges to extend learning for high performers.
                  </p>
                </div>

                <div className="bg-white/10 rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                    </svg>
                    <span>Address Growth Areas</span>
                  </h4>
                  <p className="text-blue-100 leading-relaxed">
                    Provide targeted support for{" "}
                    <strong>
                      {weaknesses.map((w) => `${w.name} (${w.code})`).join(" and ") || "—"}
                    </strong>. Consider small-group interventions, scaffolded practice, and spaced retrieval to strengthen these skills.
                  </p>
                </div>
              </div>
            </div>
          </div> */}
//         </main>
//       )}
//     </div>
//   );
// }
